import React, { useEffect, useState } from "react";
import { useGetCompanyTypes, useGetLanguages, useGetTimeZones } from "@/services/RegisterCompany";
import { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "@/components/pages/Settings/index";
import { Grid, Group } from "@mantine/core";
import Text from "@/components/atoms/Text";
import { useTranslation } from "react-i18next";
import Heading from "@/components/atoms/Heading";
import Divider from "@/components/atoms/Divider";
import { Controller, useForm } from "react-hook-form";
import TextInput from "@/components/atoms/TextInput";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CompanySettingsFormInput,
  CompanySettingsValidationSchema,
} from "@/components/pages/Settings/validation/CompanySettingsValidationSchema";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import PhoneField from "@/components/atoms/PhoneInput/PhoneInput";
import { useGetCreateOrderValues } from "@/services/Orders";
import { deleteCompanyLogo, postSaveCompanyLogo, useGetCompanyById, useGetCompanyLogo } from "@/services/Company";
import CTA from "@/components/atoms/CTA";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateCompany } from "@/services/Settings";
import { Company, DocumentBase64 } from "@/types";
import Stack from "@/components/atoms/Stack";
import Dropzone from "@/components/molecules/Dropzone";
import { MIME_TYPES } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@/contexts/AuthContext";
import { changeTimeZone } from "@/utils/sessionStorage";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import Modal, { ModalContent, ModalCTAs } from "@/components/molecules/Modal";
import Badge from "@/components/atoms/Badge";

const CompanySettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: languages } = useGetLanguages();
  const { data: companyTypes } = useGetCompanyTypes();
  const { data: timeZones } = useGetTimeZones();
  const { data: orderValues } = useGetCreateOrderValues();
  const { currentCompany } = useAuth();

  const { data: companyData } = useGetCompanyById(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<CompanySettingsFormInput>({
    resolver: zodResolver(CompanySettingsValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      companyName: companyData?.companyName || "",
      companyType: companyData?.companyType || "TRADER",
      vatNumber: companyData?.vatNumber || "",
      leiNumber: companyData?.leiNumber || "",
      eoriNumber: companyData?.eoriNumber || "",
      exportLicense: companyData?.exportLicense || "",
      phone: companyData?.phone || "",
      email: companyData?.email || "",
      language: companyData?.language || {
        id: 1,
        name: "Norwegian",
        languageCode: "no-NB",
      },
      timeZone: companyData?.timeZone || "Europe/Oslo",
      defaultCurrency: companyData?.defaultCurrency || "",
    },
  });

  const { mutateAsync: updateCompanyMutation, isPending } = useMutation({
    mutationFn: async (data: CompanySettingsFormInput) => {
      const request = {
        ...companyData,
        ...data,
        defaultCurrency: data.defaultCurrency.toUpperCase(),
      };
      return await updateCompany(request as Company);
    },
    onSuccess: (_, variables) => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.saveSuccess"),
          t("notifications:settings.updateCompany"),
          "company-updated-success"
        )
      );

      queryClient.invalidateQueries({
        queryKey: ["getCompanyById", currentCompany?.companyId],
      });
      // Company language only drives backend documents; the UI language follows the user setting.
      changeTimeZone(variables.timeZone);
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.saveError"),
          t("notifications:settings.updateCompanyError"),
          "company-updated-error"
        )
      );
    },
  });

  const onSubmit = async (data: CompanySettingsFormInput) => {
    await updateCompanyMutation(data);
  };

  return (
    <Stack data-testid="company-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.company")}
      </Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        <SettingsFormContent>
          <Grid>
            <SettingsHeader>
              <Text size="md" strong={true} display="technical" tt="uppercase" data-testid={"form-heading"}>
                {t("settings:companySettings.companyDetails")}
              </Text>
            </SettingsHeader>
            <Grid.Col span={{ base: 12, md: 6, lg: 8 }}>
              <Controller
                control={control}
                name="companyName"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="name"
                      required
                      data-testid="companyName-input"
                      label={t("common:companyName")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Controller
                control={control}
                name="companyType"
                render={({ field, fieldState }) => {
                  return (
                    <Select
                      {...field}
                      id="companyType"
                      data-testid="companyType-select"
                      label={t("common:companyType")}
                      disabled
                      data={transformDataToSelectOption(companyTypes || [], "", "", t, "enumerables:companyTypes")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Controller
                control={control}
                name="vatNumber"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="vatNumber"
                      required
                      data-testid="vatNumber-input"
                      label={t("common:vatNumber")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Controller
                control={control}
                name="leiNumber"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="leiNumber"
                      data-testid="leiNumber-input"
                      label={t("common:leiNumber")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <Controller
                control={control}
                name="exportLicense"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="exportLicense"
                      data-testid="exportLicense-input"
                      label={t("common:exportlicense")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="email"
                      required
                      data-testid="email-input"
                      label={t("common:email")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Controller
                control={control}
                name="phone"
                render={({ field, fieldState }) => {
                  return (
                    <PhoneField
                      value={field.value.toString() || ""}
                      onChange={(value) => field.onChange(value)}
                      placeholder={t("common:phone")}
                      errormessage={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <Divider />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Controller
                control={control}
                name="language"
                render={({ field, fieldState }) => {
                  return (
                    <Select
                      {...field}
                      id="language"
                      required
                      data-testid="language-select"
                      label={t("common:language")}
                      onChange={(value) => {
                        const selectedLanguage = languages?.find((lang) => lang.name === value);
                        field.onChange(selectedLanguage);
                      }}
                      data={transformDataToSelectOption(
                        languages || [],
                        "name",
                        "languageCode",
                        t,
                        "enumerables:languages"
                      )}
                      value={field.value.name}
                      error={fieldState.error?.message ? t("validation:languageRequired") : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Controller
                control={control}
                name="timeZone"
                render={({ field, fieldState }) => {
                  return (
                    <Select
                      {...field}
                      id="timeZone"
                      required
                      data-testid="timeZone-select"
                      label={t("common:timeZone")}
                      data={transformDataToSelectOption(timeZones || [], "", "", t)}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Controller
                control={control}
                name="defaultCurrency"
                render={({ field, fieldState }) => {
                  return (
                    <Select
                      {...field}
                      id="defaultCurrency"
                      required
                      data-testid="defaultCurrency-select"
                      label={t("common:currency")}
                      data={transformDataToSelectOption(
                        orderValues?.currencies || [],
                        "value",
                        "value",
                        t,
                        undefined,
                        true
                      )}
                      value={field.value?.toLowerCase() || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>

            <Grid.Col span={12}>
              <Divider />
            </Grid.Col>
          </Grid>
        </SettingsFormContent>
        <SettingsFormCTAs>
          <CTA disabled={!isValid} id="save" size="lg" type="submit" loading={isPending} data-testid="submit-btn">
            {t("actions:save")}
          </CTA>
        </SettingsFormCTAs>
      </form>
      {companyData && <OrderPlanningSection company={companyData} />}
      <CompanyLogoForm companyId={currentCompany?.companyId as number} />
    </Stack>
  );
};

interface OrderPlanningSectionProps {
  company: Company;
}

export const OrderPlanningSection: React.FC<OrderPlanningSectionProps> = ({ company }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const isOrderPlanningEnabled = company.isOrderPlanningEnabled;

  // The dedicated contracts-service endpoint is machine-to-machine only. Enabling from the
  // FE goes through the normal company-update endpoint instead — users-service persists the
  // flag and triggers the contract migration internally.
  const { mutate: enableOrderPlanning, isPending } = useMutation({
    mutationFn: () => updateCompany({ ...company, isOrderPlanningEnabled: true }),
    onSuccess: async () => {
      setConfirmModalOpen(false);
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.saveSuccess"), t("notifications:settings.orderPlanningEnabled"))
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["getCompanyById", company.id] }),
        queryClient.invalidateQueries({
          predicate: (query) =>
            typeof query.queryKey[0] === "string" && query.queryKey[0].toLowerCase().includes("contract"),
        }),
      ]);
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.saveError"), t("notifications:settings.orderPlanningEnabledError"))
      );
    },
  });

  return (
    <>
      <Modal
        opened={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title={t("settings:companySettings.orderPlanning.confirmTitle")}
      >
        <ModalContent py={20} px={23}>
          <Text>{t("settings:companySettings.orderPlanning.confirmBody")}</Text>
        </ModalContent>
        <ModalCTAs>
          <CTA intent="secondary" onClick={() => setConfirmModalOpen(false)}>
            {t("actions:cancel")}
          </CTA>
          <CTA onClick={() => enableOrderPlanning()} loading={isPending} data-testid="confirm-enable-order-planning">
            {t("settings:companySettings.orderPlanning.enableButton")}
          </CTA>
        </ModalCTAs>
      </Modal>

      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:companySettings.orderPlanning.title")}
            </Text>
          </SettingsHeader>
          <Grid.Col span={12}>
            <Group justify="space-between" align="center">
              <Stack gap={4}>
                <Text size="sm" color="secondary">
                  {t("settings:companySettings.orderPlanning.description")}
                </Text>
                <Badge color={isOrderPlanningEnabled ? "success" : "gray"}>
                  {t(
                    isOrderPlanningEnabled
                      ? "settings:companySettings.orderPlanning.statusEnabled"
                      : "settings:companySettings.orderPlanning.statusDisabled"
                  )}
                </Badge>
              </Stack>
              {!isOrderPlanningEnabled && (
                <CTA size="sm" onClick={() => setConfirmModalOpen(true)} data-testid="open-enable-order-planning-modal">
                  {t("settings:companySettings.orderPlanning.enableButton")}
                </CTA>
              )}
            </Group>
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
    </>
  );
};

export default CompanySettings;

interface CompanyLogoFormProps {
  companyId: number;
}

export const CompanyLogoForm: React.FC<CompanyLogoFormProps> = ({ companyId }) => {
  const [companyLogo, setCompanyLogo] = useState<DocumentBase64[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const { data: companyLogoData, refetch } = useGetCompanyLogo(companyId);

  useEffect(() => {
    if (companyLogoData) {
      setCompanyLogo([companyLogoData]);
    } else {
      setCompanyLogo([]);
    }
  }, [companyLogoData]);

  const { mutateAsync: mutateDeleteLogo } = useMutation({
    mutationFn: async (companyId: number) => {
      await deleteCompanyLogo(companyId);
    },
    onSuccess: async () => {
      await refetch();
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.deleteError"),
          t("notifications:settings.deleteDocumentError"),
          "company-logo-delete-error"
        )
      );
    },
  });

  const { mutateAsync: mutateSaveLogo, isPending: isPendingSaveLogo } = useMutation({
    mutationFn: async (file: File) => {
      await postSaveCompanyLogo(companyId, file);
    },
    onSuccess: async () => {
      setTimeout(() => {
        setIsLoading(false);
        refetch();
      }, 1000);
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.createError"),
          t("notifications:settings.createDocumentError"),
          "company-logo-upload-error"
        )
      );
    },
  });

  const handleSaveLogo = async (file: File[]) => {
    setIsLoading(true);
    await mutateSaveLogo(file[0]);
  };

  const handleDeleteLogo = async () => {
    await mutateDeleteLogo(companyId);
  };

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text strong={true} display="technical" tt="uppercase">
              {"Logo"}
            </Text>
          </SettingsHeader>

          <Grid.Col span={12}>
            <Dropzone
              origin="api"
              type={"dropzone"}
              buttonPosition="left"
              multiple={false}
              isLoading={isPendingSaveLogo || isLoading}
              showAsImageCarousel
              handleAdd={handleSaveLogo}
              handleDelete={handleDeleteLogo}
              buttonLabel={t("settings:uploadLogo")}
              files={companyLogo}
              accept={[MIME_TYPES.jpeg, MIME_TYPES.png, MIME_TYPES.svg]}
              maxNumberOfFiles={1}
            />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
    </React.Fragment>
  );
};
